import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, format,
  isSameMonth, isSameDay, parseISO,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Loader2, MapPin, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { STATUS_META, SERVICES_META } from "@/lib/constants";
import { formatDateShort } from "@/lib/format";
import { toast } from "sonner";

export default function Agenda() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(new Date());
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/events/${toDelete.id}`);
      setEvents((prev) => prev.filter((e) => e.id !== toDelete.id));
      toast.success("Evento excluído.");
      setToDelete(null);
    } catch {
      toast.error("Não foi possível excluir.");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    api.get("/admin/events").then(({ data }) => setEvents(data.events || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const byDate = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      if (!e.date) return;
      (map[e.date] = map[e.date] || []).push(e);
    });
    return map;
  }, [events]);

  const monthGrid = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 0 });
    const days = [];
    let d = start;
    while (d <= end) {
      days.push(d);
      d = addDays(d, 1);
    }
    return days;
  }, [cursor]);

  const groupedByMonth = useMemo(() => {
    const sorted = [...events].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    const groups = {};
    sorted.forEach((e) => {
      const key = e.date ? format(parseISO(e.date), "MMMM yyyy", { locale: ptBR }) : "Sem data";
      (groups[key] = groups[key] || []).push(e);
    });
    return groups;
  }, [events]);

  if (loading)
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[color:var(--brand-marsala)]" /></div>;

  const EventPill = ({ e }) => {
    const svcKeys = Object.keys(SERVICES_META).filter((k) => (e.services || {})[k]);
    return (
      <button
        onClick={() => navigate(`/admin/clientes/${e.id}`)}
        data-testid={`agenda-event-${e.id}`}
        title={e.couple_name}
        className="flex w-full items-center gap-1.5 rounded-md border-l-[3px] bg-[color:var(--brand-paper)] px-2 py-1.5 text-left transition-colors hover:bg-[color:var(--brand-champagne-soft)]"
        style={{ borderLeftColor: (STATUS_META[e.status] || {}).dot }}
      >
        <span className="min-w-0 flex-1 truncate text-xs font-semibold text-[color:var(--brand-graphite)]">{e.couple_name}</span>
        {svcKeys.length > 0 && (
          <span className="hidden shrink-0 gap-0.5 sm:flex">
            {svcKeys.map((k) => (
              <span key={k} className="rounded bg-white px-1 text-[9px] font-bold text-[color:var(--brand-marsala)] ring-1 ring-[color:var(--brand-border)]">{SERVICES_META[k].short}</span>
            ))}
          </span>
        )}
      </button>
    );
  };

  return (
    <div>
      <h1 className="font-serif text-3xl font-semibold text-[color:var(--brand-graphite)]">Agenda</h1>
      <p className="mt-1 text-sm text-[color:var(--brand-muted)]">Todos os casamentos aparecem na data informada pelo casal.</p>

      <Tabs defaultValue="mes" className="mt-6">
        <TabsList>
          <TabsTrigger value="mes" data-testid="agenda-tab-mes">Mês</TabsTrigger>
          <TabsTrigger value="lista" data-testid="agenda-tab-lista">Lista</TabsTrigger>
        </TabsList>

        <TabsContent value="mes" className="mt-4">
          <div className="rounded-2xl border border-[color:var(--brand-border)] bg-white p-4">
            <div className="mb-4 flex items-center justify-between">
              <button onClick={() => setCursor(addMonths(cursor, -1))} className="rounded-lg p-2 hover:bg-[color:var(--brand-paper)]"><ChevronLeft className="h-4 w-4" /></button>
              <span className="font-serif text-lg font-semibold capitalize text-[color:var(--brand-graphite)]">{format(cursor, "MMMM yyyy", { locale: ptBR })}</span>
              <button onClick={() => setCursor(addMonths(cursor, 1))} className="rounded-lg p-2 hover:bg-[color:var(--brand-paper)]"><ChevronRight className="h-4 w-4" /></button>
            </div>
            <div className="grid grid-cols-7 gap-1.5 text-center text-xs font-semibold uppercase tracking-wide text-[color:var(--brand-muted)]">
              {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((d) => <div key={d} className="py-1.5">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1.5">
              {monthGrid.map((d) => {
                const iso = format(d, "yyyy-MM-dd");
                const dayEvents = byDate[iso] || [];
                const muted = !isSameMonth(d, cursor);
                const today = isSameDay(d, new Date());
                return (
                  <div key={iso} className={`flex min-h-[112px] flex-col rounded-xl border p-1.5 ${muted ? "border-transparent bg-[color:var(--brand-paper)]/40" : "border-[color:var(--brand-border)] bg-white"} ${today ? "ring-2 ring-[color:var(--brand-marsala)]" : ""}`}>
                    <div className={`mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${today ? "bg-[color:var(--brand-marsala)] text-white" : muted ? "text-[color:var(--brand-muted)]/50" : "text-[color:var(--brand-graphite)]"}`}>{format(d, "d")}</div>
                    <div className="space-y-1">
                      {dayEvents.slice(0, 3).map((e) => <EventPill key={e.id} e={e} />)}
                      {dayEvents.length > 3 && <span className="block px-1 text-[10px] font-medium text-[color:var(--brand-muted)]">+{dayEvents.length - 3} mais</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="lista" className="mt-4 space-y-6">
          {Object.keys(groupedByMonth).length === 0 && (
            <div className="rounded-2xl border border-[color:var(--brand-border)] bg-white p-6 text-center text-sm text-[color:var(--brand-muted)]">Nenhum evento cadastrado.</div>
          )}
          {Object.entries(groupedByMonth).map(([month, list]) => (
            <div key={month}>
              <h3 className="mb-2 font-serif text-lg font-semibold uppercase tracking-wide text-[color:var(--brand-graphite)]">{month}</h3>
              <div className="space-y-2">
                {list.map((e) => (
                  <div
                    key={e.id}
                    className="flex items-center gap-2 rounded-2xl border border-[color:var(--brand-border)] bg-white p-4 transition-colors hover:bg-[color:var(--brand-paper)]"
                  >
                    <button
                      data-testid={`agenda-list-event-${e.id}`}
                      onClick={() => navigate(`/admin/clientes/${e.id}`)}
                      className="flex flex-1 items-center justify-between text-left"
                    >
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <p className="font-serif text-xl font-semibold text-[color:var(--brand-marsala)]">{e.date ? format(parseISO(e.date), "dd") : "--"}</p>
                          <p className="text-[10px] uppercase text-[color:var(--brand-muted)]">{e.date ? format(parseISO(e.date), "MMM", { locale: ptBR }) : ""}</p>
                        </div>
                        <div>
                          <p className="font-semibold text-[color:var(--brand-graphite)]">{e.couple_name}</p>
                          <p className="flex items-center gap-1 text-xs text-[color:var(--brand-muted)]"><MapPin className="h-3 w-3" /> {e.local}</p>
                          <div className="mt-1 flex gap-1">
                            {Object.keys(SERVICES_META).filter((k) => (e.services || {})[k]).map((k) => (
                              <span key={k} className="rounded bg-[color:var(--brand-champagne-soft)] px-1.5 py-0.5 text-[9px] font-semibold text-[color:var(--brand-graphite)]">{SERVICES_META[k].short}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <StatusBadge status={e.status} />
                    </button>
                    <button
                      data-testid={`agenda-delete-${e.id}`}
                      onClick={() => setToDelete(e)}
                      title="Excluir evento"
                      className="shrink-0 rounded-lg p-2 text-[color:var(--brand-danger)] transition-colors hover:bg-[#FDECEA]"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir evento?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete ? `"${toDelete.couple_name}" será removido permanentemente, junto com o repertório e as notificações. Esta ação não pode ser desfeita.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="agenda-delete-confirm"
              onClick={(ev) => { ev.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-[color:var(--brand-danger)] text-white hover:bg-[color:var(--brand-danger)]/90"
            >
              {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />} Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
